'use client';

import { useRouter } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';

import { LiveTicker } from '@/components/LiveTicker';
import { Logo } from '@/components/Logo';
import { MarketCard } from '@/components/MarketCard';
import { PullIndicator } from '@/components/PullIndicator';
import { MarketsListSkeleton } from '@/components/Skeleton';
import { usePullToRefresh } from '@/lib/use-pull-to-refresh';
import {
  createApiClient,
  type MarketListItem,
  type MeResponse,
} from '@/lib/api';
import { anonymize, categoryLabel, formatRelative, formatUsdt } from '@/lib/format';
import {
  getInitData,
  getStartParam,
  hapticImpact,
  isInTelegram,
  parseMarketStartParam,
} from '@/lib/tma';

const CATEGORIES = ['all', 'elections', 'conflicts', 'diplomacy', 'sanctions', 'domestic', 'geopolitics'] as const;
type CategoryFilter = (typeof CATEGORIES)[number];

const SORTS = [
  { key: 'closing_soon', label: 'СКОРО' },
  { key: 'volume', label: 'ОБЪЁМ' },
  { key: 'activity', label: 'АКТИВ' },
  { key: 'newest', label: 'НОВЫЕ' },
] as const;
type SortKey = (typeof SORTS)[number]['key'];

export default function MarketsPage() {
  const router = useRouter();
  const [markets, setMarkets] = useState<MarketListItem[] | null>(null);
  const [me, setMe] = useState<MeResponse['user'] | null>(null);
  // Agreement-gate теперь живёт в AgreementGate (layout-уровень) и грузит
  // /api/me сам. Здесь /api/me нужен только для balance-pill в header'е.
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<CategoryFilter>('all');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortKey>('closing_soon');
  const [tickerItems, setTickerItems] = useState<
    Array<{ user_handle: string; side: number; amount_usd: number; ago: string }>
  >([]);

  useEffect(() => {
    // Deep link: если зашли через `?startapp=market_<id>` или Telegram start_param
    // matches market_<id> — редиректим на конкретный рынок без подгрузки списка.
    const marketId = parseMarketStartParam(getStartParam());
    if (marketId) {
      router.replace(`/market?id=${encodeURIComponent(marketId)}`);
      return;
    }

    const api = createApiClient(getInitData);
    api
      .listMarkets({ limit: 50 })
      .then((res) => setMarkets(res.items))
      .catch((err) => setError(err?.message ?? 'Не удалось загрузить рынки'));
    if (isInTelegram()) {
      api
        .me()
        .then((r) => setMe(r.user))
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pull-to-refresh — async, чтобы indicator закрывался когда данные пришли.
  async function refreshFeed() {
    const api = createApiClient(getInitData);
    try {
      const res = await api.listMarkets({ limit: 50 });
      setMarkets(res.items);
      setError(null);
      if (isInTelegram()) {
        const me = await api.me().catch(() => null);
        if (me) setMe(me.user);
      }
    } catch (err: unknown) {
      const e = err as { message?: string };
      setError(e?.message ?? 'Не удалось загрузить рынки');
    }
  }
  const pull = usePullToRefresh(refreshFeed);

  // Background polling — каждые 18s обновляем markets list и ticker recent-bets.
  // Skip когда вкладка скрыта (document.hidden) чтобы не сжигать CPU/network.
  // 18s выбрано как разумный compromise: не чаще RTT (минимизирует нагрузку),
  // достаточно чтобы юзер видел изменения odds через секунды после ставки.
  useEffect(() => {
    const api = createApiClient(getInitData);
    function poll() {
      if (document.hidden) return;
      api
        .listMarkets({ limit: 50 })
        .then((r) => setMarkets(r.items))
        .catch(() => {});
      api
        .recentBets(20)
        .then((r) => {
          setTickerItems(
            r.bets.map((b) => {
              const handle = b.username ? `@${b.username}` : b.first_name;
              return {
                // Анонимизация: первая буква + '…', никогда полное имя.
                user_handle: anonymize(handle),
                side: b.side,
                amount_usd: b.amount_micro / 1_000_000,
                ago: formatRelative(b.created_at),
              };
            }),
          );
        })
        .catch(() => {});
    }
    poll(); // первый запуск сразу — заполнить ticker не дожидаясь интервала
    const id = setInterval(poll, 18_000);
    return () => clearInterval(id);
  }, []);

  const filtered = useMemo(() => {
    if (!markets) return null;
    let list = filter === 'all' ? markets : markets.filter((m) => m.category === filter);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((m) => m.question_ru.toLowerCase().includes(q));
    }
    const sorted = [...list];
    switch (sort) {
      case 'closing_soon':
        sorted.sort((a, b) => a.close_at - b.close_at);
        break;
      case 'volume':
        sorted.sort(
          (a, b) =>
            b.pool_yes_micro + b.pool_no_micro - (a.pool_yes_micro + a.pool_no_micro),
        );
        break;
      case 'activity':
        sorted.sort((a, b) => b.bets_count - a.bets_count);
        break;
      case 'newest':
        sorted.sort((a, b) => b.close_at - a.close_at);
        break;
    }
    return sorted;
  }, [markets, filter, search, sort]);

  const totalActive = markets?.filter((m) => m.state === 'open').length ?? 0;
  const totalPoolMicro =
    markets?.reduce((s, m) => s + m.pool_yes_micro + m.pool_no_micro, 0) ?? 0;
  const totalBets = markets?.reduce((s, m) => s + m.bets_count, 0) ?? 0;

  return (
    <>
      <PullIndicator distance={pull.pullDistance} active={pull.isRefreshing} />
      {/* Header — logo + balance pill */}
      <div className="px-4 pt-2 pb-3 flex items-center justify-between">
        <Logo size={28} />
        {me && (
          <a
            href="/profile/"
            className="sliced bg-accent text-bg-base px-3 py-1.5 flex items-center gap-2"
          >
            <span className="font-mono text-[11px] font-bold tabular-nums">
              {formatUsdt(me.balances.usdt_micro)}
            </span>
            <span className="display text-[11px]">+</span>
          </a>
        )}
      </div>

      {/* Stat blocks */}
      <div className="px-4 mb-3 grid grid-cols-3 gap-2">
        <div className="stat-block">
          <div className="font-mono text-[9px] text-text-tertiary tracking-[0.15em]">
            АКТИВНО
          </div>
          <div className="display text-[22px] leading-tight">{totalActive}</div>
        </div>
        <div className="stat-block no-accent">
          <div className="font-mono text-[9px] text-text-tertiary tracking-[0.15em]">
            В ПУЛАХ
          </div>
          <div className="display text-[22px] leading-tight tabular-nums">
            {formatUsdt(totalPoolMicro)}
          </div>
        </div>
        <div className="stat-block white-accent">
          <div className="font-mono text-[9px] text-text-tertiary tracking-[0.15em]">
            СТАВОК
          </div>
          <div className="display text-[22px] leading-tight tabular-nums">{totalBets}</div>
        </div>
      </div>

      {/* LIVE ticker — global recent-bets feed (polling 18s) */}
      <LiveTicker items={tickerItems} />

      {/* Search */}
      <div className="px-4 pt-3">
        <div className="relative">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="ПОИСК..."
            className="w-full bg-bg-elev border border-line px-3 py-2.5 pl-9 font-mono text-[12px] tracking-[0.05em] focus:outline-none focus:border-accent placeholder:text-text-tertiary"
          />
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary text-sm">
            🔍
          </span>
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-text-tertiary px-2"
            >
              ×
            </button>
          )}
        </div>
      </div>

      {/* Filter chips */}
      <div className="flex gap-1.5 overflow-x-auto px-4 py-3 scrollbar-hide">
        {CATEGORIES.map((c) => {
          const active = filter === c;
          const count =
            markets?.filter((m) => c === 'all' || m.category === c).length ?? 0;
          return (
            <button
              key={c}
              onClick={() => {
                hapticImpact('light');
                setFilter(c);
              }}
              className={`flex-shrink-0 px-3.5 py-2 display text-[11px] tracking-[0.05em] flex items-center gap-2 ${
                active
                  ? 'bg-accent text-bg-base'
                  : 'bg-bg-elev text-white border border-line'
              }`}
            >
              <span>{c === 'all' ? 'ВСЁ' : categoryLabel(c).toUpperCase()}</span>
              <span className="font-mono text-[10px] opacity-70 tabular-nums">{count}</span>
            </button>
          );
        })}
      </div>

      {/* Sort row */}
      <div className="flex gap-1 px-4 mb-3">
        {SORTS.map((s) => {
          const active = sort === s.key;
          return (
            <button
              key={s.key}
              onClick={() => {
                hapticImpact('light');
                setSort(s.key);
              }}
              className={`px-2.5 py-1 font-mono text-[10px] tracking-[0.1em] ${
                active ? 'bg-bg-elev2 text-accent' : 'text-text-tertiary'
              }`}
            >
              ↓ {s.label}
            </button>
          );
        })}
      </div>

      {error && (
        <div className="px-4 mb-4">
          <div className="bg-bg-elev border-l-2 border-no p-3 font-mono text-[12px] text-no">
            ⚠️ {error}
          </div>
        </div>
      )}

      {markets === null && !error && <MarketsListSkeleton />}

      {filtered && filtered.length === 0 && (
        <div className="mx-4 bg-bg-elev p-6 text-center text-text-tertiary font-mono text-[12px]">
          {search ? (
            <>
              По «<span className="text-white">{search}</span>» ничего не найдено.
            </>
          ) : markets && markets.length === 0 ? (
            'Пока нет открытых рынков. Загляни позже.'
          ) : (
            'В этой категории пока пусто.'
          )}
        </div>
      )}

      {/* Markets feed */}
      <div className="px-4 flex flex-col gap-2.5 pb-4">
        {filtered?.map((m) => (
          <MarketCard key={m.id} market={m} />
        ))}
      </div>
    </>
  );
}
